import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

/**
 * Convert a ProseMirror position to a character offset in the plain text representation.
 * Uses ProseMirror's textBetween to count characters, accounting for
 * newlines added by block elements (paragraph, heading, blockquote).
 *
 * @param doc - The ProseMirror document node
 * @param position - The ProseMirror position
 * @returns The character offset in plain text
 */
export function pmPositionToPlainTextOffset(
  doc: ProseMirrorNode,
  position: number
): number {
  return doc.textBetween(0, position, '\n').length;
}

/**
 * Convert a DOM Range to character offsets in the plain text representation.
 * Uses ProseMirror's textBetween to count characters, accounting for
 * newlines added by block elements (paragraph, heading, blockquote).
 *
 * @param range - The DOM Range from window.getSelection()
 * @param view - The ProseMirror EditorView
 * @returns Object with startOffset and endOffset, or -1 if conversion fails
 */
export function convertRangeToOffsets(
  range: Range,
  view: EditorView | undefined
): { startOffset: number; endOffset: number } {
  if (!view) {
    return { startOffset: -1, endOffset: -1 };
  }

  try {
    // Get ProseMirror positions from DOM positions
    const startPos = view.posAtDOM(range.startContainer, range.startOffset);
    const endPos = view.posAtDOM(range.endContainer, range.endOffset);

    const doc = view.state.doc;

    const startOffset = pmPositionToPlainTextOffset(doc, startPos);
    const endOffset = pmPositionToPlainTextOffset(doc, endPos);

    return { startOffset, endOffset };
  } catch (error) {
    console.error('Failed to convert range to offsets:', error);
    return { startOffset: -1, endOffset: -1 };
  }
}
